/**
=========================================================
* Material Dashboard 2 PRO React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useMemo } from "react";

// react-router components
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
// import Icon from "@mui/material/Icon";

// Material Dashboard 2 PRO React components
// import MDBox from "components/MDBox";

// Material Dashboard 2 PRO React examples
import Sidenav from "examples/Sidenav";
import Configurator from "examples/Configurator";

// Material Dashboard 2 PRO React themes
import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";

// Material Dashboard 2 PRO React Dark Mode themes
import themeDark from "assets/theme-dark";
import themeDarkRTL from "assets/theme-dark/theme-rtl";

// RTL plugins
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";

// Material Dashboard 2 PRO React routes
import routes from "routes";

// Material Dashboard 2 PRO React contexts
import { useMaterialUIController, setMiniSidenav } from "context";

// Images
import brandWhite from "assets/images/logo-ct.png";
import brandDark from "assets/images/logo-ct-dark.png";
import AddEditRegion from "layouts/masters/region/Transaction";
import AddEditDistrict from "layouts/masters/district/Transaction";
import AddEditUser from "layouts/masters/user/Transaction";
import AddEditEventCategory from "layouts/masters/eventcategory/Transactions";
import AddEditAnnualBudget from "layouts/masters/annualbudget/Transaction";
import AddEditEvent from "layouts/events/AddEdit";
import ViewNFLSRegistration from "layouts/nfls/viewnflsregistrations";
import ViewSponsorship from "layouts/sponsorship/viewsponsorship";
import NFLSCertificate from "layouts/nfls/nflscertificate";
import GenerateNFLSCertificate from "layouts/nfls/generatenflscertificate";
import SponsorshipCertificate from "layouts/sponsorship/sponsorshipcertificate";
import GenerateSponsorshipCertificate from "layouts/sponsorship/generatesponsorshipcertificate";
import SponsorshipWorkOrder from "layouts/sponsorship/sponsorshipworkorder";
import AddEditQuotation from "layouts/events/Quotations";
import AddEditWorkOrder from "layouts/events/WorkOrder";
import AddEditInvoice from "layouts/events/Invoice";
import AddEditPayment from "layouts/events/Payment";
import RequisiteInformationReport from "layouts/sponsorship/requisiteinformationreport";
import SponsorshipInvoice from "layouts/sponsorship/sponsorshipinvoice";
import SponsorshipPayment from "layouts/sponsorship/sponsorshippayment";

export default function App() {
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    direction,
    layout,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
  } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  // Check authentication
  const userData = sessionStorage.getItem('userData');

  useEffect(() => {
    if (userData == null) {
      navigate('/authentication/sign-in/basic');
    }
  }, []);
  // Cache for the rtl
  useMemo(() => {
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [rtlPlugin],
    });

    setRtlCache(cacheRtl);
  }, []);

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  // Change the openConfigurator state
  // const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }

      if (route.route) {
        return <Route exact path={route.route} element={route.component} key={route.key} />;
      }

      return null;
    });

  // const configsButton = (
  //   <MDBox
  //     display="flex"
  //     justifyContent="center"
  //     alignItems="center"
  //     width="3.25rem"
  //     height="3.25rem"
  //     bgColor="white"
  //     shadow="sm"
  //     borderRadius="50%"
  //     position="fixed"
  //     right="2rem"
  //     bottom="2rem"
  //     zIndex={99}
  //     color="dark"
  //     sx={{ cursor: "pointer" }}
  //     onClick={handleConfiguratorOpen}
  //   >
  //     <Icon fontSize="small" color="inherit">
  //       settings
  //     </Icon>
  //   </MDBox>
  // );

  return direction === "rtl" ? (
    <CacheProvider value={rtlCache}>
      <ThemeProvider theme={darkMode ? themeDarkRTL : themeRTL}>
        <CssBaseline />
        {layout === "dashboard" && (
          <>
            <Sidenav
              color={sidenavColor}
              brand={(transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite}
              brandName="NFLS"
              routes={routes}
              onMouseEnter={handleOnMouseEnter}
              onMouseLeave={handleOnMouseLeave}
            />
            <Configurator />
            {/* {configsButton} */}
          </>
        )}
        {layout === "vr" && <Configurator />}
        <Routes>
          {getRoutes(routes)}
          <Route path="*" element={<Navigate to="/dashboards/analytics" />} />
          <Route path="/addeditregion/:id" element={<AddEditRegion />} />
          <Route path="/addeditdistrict/:id" element={<AddEditDistrict />} />          
          <Route path="/addedituser/:id" element={<AddEditUser />} />
          <Route path="/addediteventcategory/:id" element={<AddEditEventCategory />} />
          <Route path="/addeditannualbudget/:id" element={<AddEditAnnualBudget />} />
          <Route path="/addeditevent/:id" element={<AddEditEvent />} />
          <Route path="/viewnflsregistration/:id" element={<ViewNFLSRegistration />} />
          <Route path="/viewsponsorship/:id" element={<ViewSponsorship />} />
          <Route path="/nflscertificate/:id" element={<NFLSCertificate />} />          
          <Route path="/generatenflscertificate/:id" element={<GenerateNFLSCertificate />} />          
          <Route path="/sponsorshipcertificate/:id" element={<SponsorshipCertificate />} />          
          <Route path="/generatesponsorshipcertificate/:id" element={<GenerateSponsorshipCertificate />} />          
          <Route path="/sponsorshipworkorder/:id" element={<SponsorshipWorkOrder />} />              
          <Route path="/addeditquotations/:id" element={<AddEditQuotation />} />              
          <Route path="/addeditworkorder/:quotationId/:eventId" element={<AddEditWorkOrder />} />              
          <Route path="/addeditinvoice/:workOrderId/:eventId" element={<AddEditInvoice />} />              
          <Route path="/addeditpayment/:workOrderId/:eventId" element={<AddEditPayment />} />    
          <Route path="/requisiteinformationreport/:id" element={<RequisiteInformationReport />} />          
          <Route path="/sponsorshipinvoice/:id" element={<SponsorshipInvoice />} />          
          <Route path="/sponsorshippayment/:id" element={<SponsorshipPayment />} />          
        </Routes>
      </ThemeProvider>
    </CacheProvider>
  ) : (
    <ThemeProvider theme={darkMode ? themeDark : theme}>
      <CssBaseline />
      {layout === "dashboard" && (
        <>
          <Sidenav
            color={sidenavColor}
            brand={(transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite}
            brandName="NFLS"
            routes={routes}
            onMouseEnter={handleOnMouseEnter}
            onMouseLeave={handleOnMouseLeave}
          />
          <Configurator />
          {/* {configsButton} */}
        </>
      )}
      {layout === "vr" && <Configurator />}
      <Routes>
        {getRoutes(routes)}
        <Route path="*" element={<Navigate to="/dashboards/analytics" />} />
        <Route path="/addeditregion/:id" element={<AddEditRegion />} />
          <Route path="/addeditdistrict/:id" element={<AddEditDistrict />} />          
          <Route path="/addedituser/:id" element={<AddEditUser />} />
          <Route path="/addediteventcategory/:id" element={<AddEditEventCategory />} />
          <Route path="/addeditannualbudget/:id" element={<AddEditAnnualBudget />} />
          <Route path="/addeditevent/:id" element={<AddEditEvent />} />
          <Route path="/viewnflsregistration/:id" element={<ViewNFLSRegistration />} />
          <Route path="/viewsponsorship/:id" element={<ViewSponsorship />} />
          <Route path="/nflscertificate/:id" element={<NFLSCertificate />} />          
          <Route path="/generatenflscertificate/:id" element={<GenerateNFLSCertificate />} />          
          <Route path="/sponsorshipcertificate/:id" element={<SponsorshipCertificate />} />          
          <Route path="/generatesponsorshipcertificate/:id" element={<GenerateSponsorshipCertificate />} />          
          <Route path="/sponsorshipworkorder/:id" element={<SponsorshipWorkOrder />} />   
          <Route path="/addeditquotations/:id" element={<AddEditQuotation />} />      
          <Route path="/addeditworkorder/:quotationId/:eventId" element={<AddEditWorkOrder />} />  
          <Route path="/addeditinvoice/:workOrderId/:eventId" element={<AddEditInvoice />} />              
          <Route path="/addeditpayment/:workOrderId/:eventId" element={<AddEditPayment />} />  
          <Route path="/requisiteinformationreport/:id" element={<RequisiteInformationReport />} />  
          <Route path="/sponsorshipinvoice/:id" element={<SponsorshipInvoice />} />          
          <Route path="/sponsorshippayment/:id" element={<SponsorshipPayment />} />                                                             
      </Routes>
    </ThemeProvider>
  );
}
